'use client';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, Ticket } from 'lucide-react';

interface Purchase {
	id: string;
	quantity: number;
	total_price: number;
	status: string;
	payment_status: string;
	created_at: string;
	guest_email: string;
	guest_name: string;
	event_tickets: {
		id: string;
		name: string;
		price: number;
	};
	events: {
		id: string;
		name: string;
		description: string;
		image_url: string;
		start_date: string;
		time: string;
		venue: string;
		location: string;
	};
}

export default function GuestPurchaseLookup() {
	const [email, setEmail] = useState('');
	const [purchaseId, setPurchaseId] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [purchase, setPurchase] = useState<Purchase | null>(null);

	const handleLookup = async () => {
		if (!email.trim() || !purchaseId.trim()) {
			toast.error('Please enter both email and purchase ID');
			return;
		}

		setIsLoading(true);
		try {
			const response = await fetch('/api/guest-purchases', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					email: email.trim(),
					purchaseId: purchaseId.trim(),
				}),
			});

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.error || 'Failed to lookup purchase');
			}

			setPurchase(data.purchase);
			toast.success('Purchase found!');
		} catch (error) {
			console.error('Lookup error:', error);
			toast.error(
				error instanceof Error ? error.message : 'Failed to lookup purchase'
			);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className='container mx-auto px-4 py-8 max-w-2xl'>
			<div className='text-center mb-8'>
				<h1 className='text-3xl font-bold mb-2'>Guest Purchase Lookup</h1>
				<p className='text-muted-foreground'>
					Enter your email and purchase ID to view your ticket details
				</p>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Find Your Purchase</CardTitle>
					<CardDescription>
						Enter the email address and purchase ID from your confirmation email
					</CardDescription>
				</CardHeader>
				<CardContent className='space-y-4'>
					<div>
						<Label htmlFor='email'>Email Address</Label>
						<Input
							id='email'
							type='email'
							placeholder='Enter your email address'
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>
					</div>
					<div>
						<Label htmlFor='purchase-id'>Purchase ID</Label>
						<Input
							id='purchase-id'
							type='text'
							placeholder='Enter your purchase ID'
							value={purchaseId}
							onChange={(e) => setPurchaseId(e.target.value)}
						/>
					</div>
					<Button
						onClick={handleLookup}
						disabled={isLoading}
						className='w-full'
					>
						{isLoading ? (
							<>
								<Loader2 className='mr-2 h-4 w-4 animate-spin' />
								Looking up...
							</>
						) : (
							'Lookup Purchase'
						)}
					</Button>
				</CardContent>
			</Card>

			{purchase && (
				<Card className='mt-6'>
					<CardHeader>
						<CardTitle className='flex items-center gap-2'>
							<Ticket className='h-5 w-5' />
							Purchase Details
						</CardTitle>
					</CardHeader>
					<CardContent className='space-y-4'>
						<div className='grid grid-cols-2 gap-4'>
							<div>
								<Label className='text-sm font-medium text-muted-foreground'>
									Event
								</Label>
								<p className='font-medium'>{purchase.events.name}</p>
							</div>
							<div>
								<Label className='text-sm font-medium text-muted-foreground'>
									Ticket Type
								</Label>
								<p className='font-medium'>{purchase.event_tickets.name}</p>
							</div>
							<div>
								<Label className='text-sm font-medium text-muted-foreground'>
									Quantity
								</Label>
								<p className='font-medium'>{purchase.quantity}</p>
							</div>
							<div>
								<Label className='text-sm font-medium text-muted-foreground'>
									Total Paid
								</Label>
								<p className='font-medium'>
									{purchase.total_price === 0
										? 'Free'
										: `GH₵ ${purchase.total_price.toFixed(2)}`}
								</p>
							</div>
							<div>
								<Label className='text-sm font-medium text-muted-foreground'>
									Status
								</Label>
								<p className='font-medium capitalize'>{purchase.status}</p>
							</div>
							<div>
								<Label className='text-sm font-medium text-muted-foreground'>
									Payment Status
								</Label>
								<p className='font-medium capitalize'>
									{purchase.payment_status}
								</p>
							</div>
						</div>

						<div className='pt-4 border-t'>
							<Label className='text-sm font-medium text-muted-foreground'>
								Event Details
							</Label>
							<div className='mt-2 space-y-1'>
								<p>
									<strong>Date:</strong>{' '}
									{new Date(purchase.events.start_date).toLocaleDateString()}
								</p>
								<p>
									<strong>Time:</strong> {purchase.events.time}
								</p>
								{purchase.events.venue && (
									<p>
										<strong>Venue:</strong> {purchase.events.venue}
									</p>
								)}
								{purchase.events.location && (
									<p>
										<strong>Location:</strong> {purchase.events.location}
									</p>
								)}
							</div>
						</div>

						{purchase.status === 'completed' && (
							<div className='pt-4 border-t bg-green-50 p-4 rounded-lg'>
								<p className='text-green-800 font-medium'>
									✅ Your tickets are confirmed! You'll receive an email with
									entry details closer to the event date.
								</p>
							</div>
						)}
					</CardContent>
				</Card>
			)}
		</div>
	);
}
